import { useState, useCallback, useMemo } from 'react';
import { Service } from '@/types/service';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation } from '@tanstack/react-query';

export function useServices() {
  const [previousStatuses, setPreviousStatuses] = useState<Map<string, Service['status']>>(new Map());

  // Fetch services and their initial stored status
  const { data: initialServices = [], isLoading: loadingServices, refetch } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      if (!isSupabaseConfigured || !supabase) {
        return [];
      }
      
      // Load services
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, name, url, category, description')
        .eq('is_active', true)
        .order('display_order');

      if (servicesError) throw servicesError;

      // Load latest history
      const { data: statusData, error: statusError } = await supabase
        .from('service_status_history')
        .select('*');

      if (statusError) {
        console.error('Error loading status history:', statusError);
      }

      // Map status data by service_id
      const statusMap = new Map(
        (statusData || []).map(s => [s.service_id, s])
      );

      const servicesWithStatus: Service[] = (servicesData || []).map(service => {
        const status = statusMap.get(service.id);
        return {
          id: service.id,
          name: service.name,
          description: service.description || `Status monitoring for ${service.name}`,
          url: service.url,
          category: service.category,
          status: (status?.status as Service['status']) || 'unknown',
          responseTime: status?.response_time,
          lastChecked: status?.checked_at ? new Date(status.checked_at) : undefined,
          confidence: status?.confidence as Service['confidence'],
          checkMethod: status?.check_method as Service['checkMethod'],
          statusDescription: status?.status_description,
          rawIndicator: status?.raw_indicator,
        };
      });

      // Initialize previous statuses
      const statusesMap = new Map<string, Service['status']>();
      servicesWithStatus.forEach(service => {
        statusesMap.set(service.id, service.status);
      });
      setPreviousStatuses(statusesMap);

      return servicesWithStatus;
    }
  });

  // Mutation to check services via server-side edge function
  const checkMutation = useMutation({
    mutationFn: async (servicesToCheck: Service[]) => {
      const checkStartTime = Date.now();
      
      // Dynamically import to avoid loading until needed (code splitting)
      const { checkServicesServerSide } = await import('@/utils/parseServices');
      
      const toastId = toast.loading('Checking services...', {
        description: `0 of ${servicesToCheck.length} checked`,
      });

      const results = await checkServicesServerSide(
        servicesToCheck.map(s => ({ id: s.id, name: s.name, url: s.url })),
        (current, total) => {
          toast.loading('Checking services...', {
            id: toastId,
            description: `${current} of ${total} checked`,
          });
        }
      );

      const checkDuration = Date.now() - checkStartTime;

      // Map results back to services
      const updatedServices = servicesToCheck.map((service, index) => {
        const result = results[index];
        return {
          ...service,
          status: result.status,
          responseTime: result.responseTime,
          lastChecked: new Date(),
          confidence: result.confidence,
          checkMethod: result.checkMethod,
          statusDescription: result.statusDescription,
          rawIndicator: result.rawIndicator,
        };
      });

      toast.dismiss(toastId);
      return { updatedServices, checkDuration };
    },
    onSuccess: ({ updatedServices, checkDuration }) => {
      // Create new statuses map for comparison
      const newStatusesMap = new Map<string, Service['status']>();
      updatedServices.forEach(service => {
        newStatusesMap.set(service.id, service.status);
      });

      // Detect status changes for notifications
      const recovered = updatedServices.filter(service => {
        const prevStatus = previousStatuses.get(service.id);
        return prevStatus && (prevStatus === 'down' || prevStatus === 'degraded') && service.status === 'healthy';
      });

      const newlyDown = updatedServices.filter(service => {
        const prevStatus = previousStatuses.get(service.id);
        return prevStatus === 'healthy' && (service.status === 'down' || service.status === 'degraded');
      });

      // Update previous statuses for next comparison
      setPreviousStatuses(newStatusesMap);

      // Show appropriate toast based on results
      const healthyCount = updatedServices.filter(s => s.status === 'healthy').length;
      const degradedCount = updatedServices.filter(s => s.status === 'degraded').length;
      const downCount = updatedServices.filter(s => s.status === 'down').length;

      toast.success('Service check complete', {
        description: `${healthyCount} healthy, ${degradedCount} degraded, ${downCount} down (${(checkDuration / 1000).toFixed(1)}s)`,
      });

      // Show recovery notifications
      if (recovered.length > 0) {
        recovered.forEach(service => {
          toast.success(`${service.name} recovered`, {
            description: 'Service is now healthy',
          });
        });
      }

      // Show degradation notifications
      if (newlyDown.length > 0) {
        newlyDown.forEach(service => {
          const isDown = service.status === 'down';
          toast.error(`${service.name} is ${service.status}`, {
            description: isDown ? 'Service is currently down' : 'Service experiencing issues',
          });
        });
      }

      // Refetch to update the query cache with new statuses
      refetch();
    },
    onError: (error) => {
      console.error('Check failed', error);
      toast.error('Failed to check services', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  });

  // Use initialServices directly as services (React Query handles the state)
  const services = initialServices;

  const stats = useMemo(() => ({
    total: services.length,
    healthy: services.filter(s => s.status === 'healthy').length,
    degraded: services.filter(s => s.status === 'degraded').length,
    down: services.filter(s => s.status === 'down').length,
    unknown: services.filter(s => s.status === 'unknown').length,
  }), [services]);

  const checkAllServices = useCallback(() => {
    if (services.length === 0) {
      toast.error('No services to check');
      return;
    }
    checkMutation.mutate(services);
  }, [checkMutation, services]);

  const setServices = useCallback((_newServices: Service[] | ((prev: Service[]) => Service[])) => {
    // This was used for optimistic updates or manual setting in the old hook.
    // With React Query, we should essentially update the cache.
    console.warn('setServices is deprecated in favor of React Query');
  }, []);

  return {
    services,
    loading: loadingServices,
    checking: checkMutation.isPending,
    checkAllServices,
    setServices,
    stats
  };
}
